<script setup lang="ts">
import type Vehicle from '@/classes/vehicle_classes/vehicle'
import {
  GettersTypes,
  MutationTypes,
  type DashboardDialogContent,
  type State,
} from '@/store/store_types'
import { computed, nextTick, ref, watch } from 'vue'
import { useDisplay } from 'vuetify'
import { useStore } from 'vuex'
import ChargingStationDetailsContent from '../charging-stations/ChargingStationDetailsContent.vue'
import LocationDetails from '../location-manager/LocationDetails.vue'
import LocationManagerMain from '../location-manager/LocationManagerMain.vue'
import LocationsPage from '../location-manager/LocationsPage.vue'
import EditUserContent from '../user/profile/EditUserContent.vue'
import VehicleDetails from '../vehicles/VehicleDetails.vue'
import VehicleList from '../vehicles/VehicleList.vue'
import DashboardBreadcrumbs, { type BreadcrumbsItem } from './DashboardBreadcrumbs.vue'
import DashboardMain from './DashboardMain.vue'
import DashboardMobileAppBar from './DashboardMobileAppBar.vue'
import MyFleetContent from './MyFleetContent.vue'
import SavedTripsContent from './SavedTripsContent.vue'
import TripPlanningDefaults from './TripPlanningDefaults.vue'
import BookingAndChargingHistory from './booking-and-charging/BookingAndChargingHistory.vue'
import CompletedBookingDetails from './booking-and-charging/CompletedBookingDetails.vue'
import UpcomingBookingDetails from './booking-and-charging/UpcomingBookingDetails.vue'
import UpcomingBookings from './booking-and-charging/UpcomingBookings.vue'

const { activator } = defineProps<{ activator: boolean }>()
const emit = defineEmits<{ (e: 'close'): void }>()

const { mobile } = useDisplay()

const store = useStore<State>()
const tab = computed<DashboardDialogContent | undefined>(() => store.state.dashboardDialogContent)
const vehicleId = ref<string | undefined>(undefined)
const vehiclesExpand = ref<number | undefined>(undefined)
const bookingsExpand = ref<number | undefined>(undefined)
const showingTrip = ref(false)
const vehicleData = computed<Vehicle | undefined>(
  () => store.getters[GettersTypes.selectedVehicleData],
)
const isSelected = computed<boolean>(
  () => vehicleData.value?.localId === store.state.selectedVehicle,
)
function emitClose() {
  emit('close')
}

function keyDown(event: KeyboardEvent) {
  if (event.key === 'Escape') emitClose()
}

function resetScrollPosition() {
  nextTick(() => {
    const myDiv = document.getElementById('dashboard-scroll-container')
    if (myDiv) myDiv.scrollTop = 0
  })
}

// navigation methods
function viewVehicle(id: string | undefined) {
  vehicleId.value = id
  vehiclesExpand.value = 0
  store.commit(MutationTypes.setDashboardDialogContent, 'vehicle-details')
  resetScrollPosition()
}

function viewAllVehicles() {
  store.commit(MutationTypes.setDashboardDialogContent, 'fleet-vehicles')
  vehiclesExpand.value = 0
  resetScrollPosition()
}

function viewDashboardContentWithBookingExpansion(content: DashboardDialogContent) {
  bookingsExpand.value = 0
  viewDashboardContent(content)
}

function viewDashboardContent(content: DashboardDialogContent) {
  store.commit(MutationTypes.setDashboardDialogContent, content)
  resetScrollPosition()
}

function breadcrumbNavigation(item: BreadcrumbsItem) {
  if (item.disabled) return
  if (item.text === 'Dashboard') {
    viewDashboardContent('home')
  } else if (item.text === 'Fleet vehicles') {
    viewAllVehicles()
  } else if (item.text === 'Edit trip planning presets') {
    viewDashboardContent('defaults')
  } else if (item.text === 'Manage account') {
    viewDashboardContent('account')
  } else if (item.text === 'Saved stations & locations') {
    viewDashboardContent('locations-manager')
  } else if (item.text === 'Saved charging stations') {
    viewDashboardContent('saved-charger-list')
  } else if (item.text === 'Charging station details') {
    viewDashboardContent('saved-charger-details')
  } else if (item.text === 'Saved locations') {
    viewDashboardContent('saved-location-list')
  } else if (item.text === 'Saved location details') {
    viewDashboardContent('saved-location-details')
  } else if (item.text === 'My fleet') {
    viewDashboardContent('my-fleet')
  } else if (item.text === 'Vehicle details') {
    viewVehicle(undefined)
  } else if (item.text === 'Saved trips') {
    viewDashboardContent('saved-trips')
  } else if (item.text === 'Upcoming bookings') {
    viewDashboardContentWithBookingExpansion('upcoming-bookings')
  } else if (item.text === 'Upcoming booking details') {
    viewDashboardContentWithBookingExpansion('upcoming-booking-details')
  } else if (item.text === 'Booking & charging history') {
    viewDashboardContentWithBookingExpansion('booking-and-charging-history')
  } else if (item.text === 'Completed booking') {
    viewDashboardContentWithBookingExpansion('completed-booking-details')
  }
}

const DashboardBreadcrumbItems = computed<BreadcrumbsItem[]>(() => {
  let typedBreadcrumbItems: BreadcrumbsItem[] = []

  switch (tab.value) {
    case 'home':
      typedBreadcrumbItems = [] as BreadcrumbsItem[]
      break
    case 'account':
      typedBreadcrumbItems = [{ text: 'Dashboard' }, { text: 'Manage account', disabled: true }]
      break
    case 'fleet-vehicles':
      typedBreadcrumbItems = [{ text: 'Dashboard' }, { text: 'Fleet vehicles', disabled: true }]
      break
    case 'vehicle-details':
      typedBreadcrumbItems = [
        { text: 'Dashboard' },
        { text: 'Fleet vehicles' },
        { text: 'Vehicle details', disabled: true },
      ]
      break
    case 'defaults':
      typedBreadcrumbItems = [
        { text: 'Dashboard' },
        { text: 'Edit trip planning presets', disabled: true },
      ]
      break
    case 'saved-trips':
      typedBreadcrumbItems = showingTrip.value
        ? [{ text: 'Dashboard' }, { text: 'Saved trips' }, { text: 'Trip details', disabled: true }]
        : [{ text: 'Dashboard' }, { text: 'Saved trips', disabled: true }]
      break
    case 'locations-manager':
      typedBreadcrumbItems = [
        { text: 'Dashboard' },
        { text: 'Saved stations & locations', disabled: true },
      ]
      break
    case 'saved-charger-list':
      typedBreadcrumbItems = [
        { text: 'Dashboard' },
        { text: 'Saved stations & locations' },
        { text: 'All saved charging stations', disabled: true },
      ]
      break
    case 'saved-charger-details':
      typedBreadcrumbItems = [
        { text: 'Dashboard' },
        { text: 'Saved stations & locations' },
        { text: 'Saved charging stations' },
        { text: 'Charging station details', disabled: true },
      ]
      break
    case 'saved-location-list':
      typedBreadcrumbItems = [
        { text: 'Dashboard' },
        { text: 'Saved stations & locations' },
        { text: 'All saved locations', disabled: true },
      ]
      break
    case 'saved-location-details':
      typedBreadcrumbItems = [
        { text: 'Dashboard' },
        { text: 'Saved stations & locations' },
        { text: 'Saved locations' },
        { text: 'Location details', disabled: true },
      ]
      break
    case 'upcoming-bookings':
      typedBreadcrumbItems = [{ text: 'Dashboard' }, { text: 'Upcoming bookings', disabled: true }]
      break
    case 'upcoming-booking-details':
      typedBreadcrumbItems = [
        { text: 'Dashboard' },
        { text: 'Upcoming bookings' },
        { text: 'Booking details', disabled: true },
      ]
      break
    case 'booking-and-charging-history':
      typedBreadcrumbItems = [
        { text: 'Dashboard' },
        { text: 'Booking & charging history', disabled: true },
      ]
      break
    case 'completed-booking-details':
      typedBreadcrumbItems = [
        { text: 'Dashboard' },
        { text: 'Booking & charging history' },
        { text: 'Completed booking', disabled: true },
      ]
      break
    default:
      typedBreadcrumbItems = [{ text: 'Dashboard' }]
  }

  return typedBreadcrumbItems
})

// watchers
watch(tab, (value: DashboardDialogContent | undefined) => {
  // expand vehicles accordion if needed
  if (value === 'vehicle-details' || value === 'fleet-vehicles') {
    vehiclesExpand.value = 0
  } else {
    vehiclesExpand.value = undefined
  }
  // expand bookings accordion if needed
  if (
    value === 'upcoming-booking-details' ||
    value === 'booking-and-charging-history' ||
    value === 'upcoming-bookings' ||
    value === 'completed-booking-details'
  ) {
    bookingsExpand.value = 0
  } else {
    bookingsExpand.value = undefined
  }
})

watch(
  () => activator,
  () => {
    resetScrollPosition()
  },
)
</script>

<template>
  <v-dialog
    :model-value="activator"
    :width="mobile ? '100%' : '80%'"
    :height="mobile ? '100%' : '90%'"
    @click:outside="emit('close')"
    @keydown="keyDown($event)"
    content-class="rounded-lg"
    :fullscreen="mobile"
    style="z-index: 1200"
  >
    <div class="modal-content rounded-lg">
      <div v-if="!mobile" class="side-nav-column pwt-scrollbar-styles">
        <!-- Side nav content here -->
        <v-tabs
          direction="vertical"
          hide-slider
          :mandatory="false"
          :model-value="tab"
          @change="resetScrollPosition"
        >
          <v-tab
            class="text-none"
            tab-value="home"
            :class="tab === 'home' ? 'bg-pwt_azure_20' : ''"
            @click="() => viewDashboardContent('home')"
          >
            Home
          </v-tab>
          <v-tab
            class="text-none"
            tab-value="account"
            :class="tab === 'account' ? 'bg-pwt_azure_20' : ''"
            @click="() => viewDashboardContent('account')"
          >
            Account
          </v-tab>
          <v-tab
            class="text-none"
            tab-value="my-fleet"
            :class="tab === 'my-fleet' ? 'bg-pwt_azure_20' : ''"
            @click="() => viewDashboardContent('my-fleet')"
          >
            My Fleet
          </v-tab>
          <v-expansion-panels hide-actions flat variant="accordion" v-model="vehiclesExpand">
            <v-expansion-panel>
              <v-expansion-panel-title
                max-height="48px"
                height="48px"
                class="v-tab text-none pl-4"
                :class="
                  tab === 'vehicle-details' || tab === 'fleet-vehicles'
                    ? 'bg-pwt_azure_20'
                    : 'bg-pwt_snow'
                "
              >
                Vehicles
              </v-expansion-panel-title>
              <v-expansion-panel-text class="bg-pwt_snow">
                <v-tab
                  class="text-none pl-2"
                  width="100%"
                  @click="viewAllVehicles"
                  :class="tab === 'fleet-vehicles' ? 'text-decoration-underline' : ''"
                >
                  Fleet vehicles
                </v-tab>
                <v-tab
                  class="text-none pl-2"
                  width="100%"
                  :class="tab === 'vehicle-details' ? 'text-decoration-underline' : ''"
                  tab-value="vehicle-details"
                  @click="viewVehicle(undefined)"
                >
                  Vehicle details
                </v-tab>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
          <v-tab
            class="text-none"
            :class="tab === 'defaults' ? 'bg-pwt_azure_20' : ''"
            tab-value="defaults"
            @click="() => viewDashboardContent('defaults')"
          >
            Trip planning defaults
          </v-tab>
          <v-tab
            class="text-none"
            :class="tab === 'saved-trips' ? 'bg-pwt_azure_20' : ''"
            tab-value="saved-trips"
            @click="() => viewDashboardContent('saved-trips')"
          >
            Saved trips
          </v-tab>
          <v-tab
            class="text-none"
            :class="
              tab === 'locations-manager' ||
              tab === 'saved-charger-details' ||
              tab === 'saved-location-details' ||
              tab === 'saved-charger-list' ||
              tab === 'saved-location-list'
                ? 'bg-pwt_azure_20'
                : ''
            "
            tab-value="locations-manager"
            @click="() => viewDashboardContent('locations-manager')"
          >
            Saved stations & locations
          </v-tab>
          <v-expansion-panels hide-actions flat variant="accordion" v-model="bookingsExpand">
            <v-expansion-panel>
              <v-expansion-panel-title
                height="48px"
                class="pl-4 text-none"
                :class="
                  tab === 'upcoming-bookings' || tab === 'booking-and-charging-history'
                    ? 'bg-pwt_azure_20'
                    : ''
                "
              >
                Booking & charging
              </v-expansion-panel-title>
              <v-expansion-panel-text class="bg-pwt_snow pl-0">
                <v-tab
                  class="text-none pl-2"
                  :class="
                    tab === 'upcoming-bookings' || tab === 'upcoming-booking-details'
                      ? 'text-decoration-underline'
                      : ''
                  "
                  width="100%"
                  tab-value="upcoming-bookings"
                  @click="() => viewDashboardContentWithBookingExpansion('upcoming-bookings')"
                >
                  Upcoming bookings
                </v-tab>
                <v-tab
                  class="text-none pl-2"
                  :class="tab === 'booking-and-charging-history' ? 'text-decoration-underline' : ''"
                  width="100%"
                  @click="
                    () => viewDashboardContentWithBookingExpansion('booking-and-charging-history')
                  "
                  tab-value="booking-and-charging-history"
                >
                  Booking & charging history
                </v-tab>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-tabs>
      </div>
      <div class="display-column" :class="{ 'display-column--mobile': mobile }">
        <div class="exit-nav-bar">
          <!-- Nav bar content here -->
          <v-btn density="compact" flat icon color="transparent" @click="emit('close')">
            <v-icon size="20" color="pwt_light_grey"> mdi-close </v-icon>
          </v-btn>
        </div>
        <div v-if="mobile" class="sticky-nav">
          <!-- Side nav content here -->
          <DashboardMobileAppBar
            @close="emitClose"
            @view-home="() => viewDashboardContent('home')"
            @view-account="() => viewDashboardContent('account')"
            @view-vehicle="viewVehicle"
            @view-vehicles="viewAllVehicles"
            @view-defaults="() => viewDashboardContent('defaults')"
            @view-locations="() => viewDashboardContent('locations-manager')"
            @view-saved-trips="() => viewDashboardContent('saved-trips')"
            :tab="tab"
          />
        </div>
        <div v-if="!mobile && tab !== 'home'" class="sticky-nav">
          <DashboardBreadcrumbs
            :items="DashboardBreadcrumbItems"
            @navigation="breadcrumbNavigation"
            @back="breadcrumbNavigation({ text: 'Dashboard' })"
          />
        </div>
        <div
          class="pwt-scrollbar-styles"
          :class="
            tab === 'home'
              ? 'display-column--scroll-container--home-tab'
              : 'display-column--scroll-container'
          "
        >
          <!-- Display content here -->
          <v-tabs-window :model-value="tab" class="px-4">
            <v-tabs-window-item value="home">
              <DashboardMain
                @view-vehicle="viewVehicle"
                @view-account="() => viewDashboardContent('account')"
                @view-trip="emitClose"
                @view-all-vehicles="viewAllVehicles"
                @view-locations="() => viewDashboardContent('locations-manager')"
                @view-defaults="() => viewDashboardContent('defaults')"
                @view-saved-trips="() => viewDashboardContent('saved-trips')"
                @view-my-fleet="() => viewDashboardContent('my-fleet')"
                @logout="emitClose"
              />
            </v-tabs-window-item>
            <v-tabs-window-item value="account">
              <EditUserContent />
            </v-tabs-window-item>
            <v-tabs-window-item value="my-fleet">
              <MyFleetContent
                @view-locations="() => viewDashboardContent('locations-manager')"
                @view-vehicles="viewAllVehicles"
                @view-vehicle="viewVehicle"
              />
            </v-tabs-window-item>
            <v-tabs-window-item value="vehicle-details">
              <VehicleDetails :vehicle="vehicleData" :isSelected="isSelected" />
            </v-tabs-window-item>
            <v-tabs-window-item value="fleet-vehicles">
              <VehicleList @view-vehicle="viewVehicle($event.localId)" />
            </v-tabs-window-item>
            <v-tabs-window-item value="defaults">
              <TripPlanningDefaults />
            </v-tabs-window-item>
            <v-tabs-window-item value="saved-trips">
              <SavedTripsContent
                @close="emitClose"
                @showing-trip="
                  ($event) => {
                    showingTrip = $event
                  }
                "
              />
            </v-tabs-window-item>
            <v-tabs-window-item value="locations-manager">
              <LocationManagerMain />
            </v-tabs-window-item>
            <v-tabs-window-item value="saved-charger-list">
              <LocationsPage location-type="saved-charger" />
            </v-tabs-window-item>
            <v-tabs-window-item value="saved-charger-details">
              <ChargingStationDetailsContent variant="dashboard" class="mt-10" />
            </v-tabs-window-item>
            <v-tabs-window-item value="saved-location-list">
              <LocationsPage location-type="fav-location" />
            </v-tabs-window-item>
            <v-tabs-window-item value="saved-location-details">
              <LocationDetails />
            </v-tabs-window-item>
            <v-tabs-window-item value="upcoming-bookings">
              <UpcomingBookings />
            </v-tabs-window-item>
            <v-tabs-window-item value="booking-and-charging-history">
              <BookingAndChargingHistory />
            </v-tabs-window-item>
            <v-tabs-window-item value="upcoming-booking-details">
              <UpcomingBookingDetails />
            </v-tabs-window-item>
            <v-tabs-window-item value="completed-booking-details">
              <CompletedBookingDetails />
            </v-tabs-window-item>
          </v-tabs-window>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<style scoped>
.pwt-scrollbar-styles {
  scrollbar-color: #ffffff #e0e0e0;
  scrollbar-width: thin;
}

.pwt-scrollbar-styles:hover {
  scrollbar-color: #eeeeee #e0e0e0;
}

.pwt-scrollbar-styles::-webkit-scrollbar {
  width: 6px;
}

.pwt-scrollbar-styles::-webkit-scrollbar-track {
  background: #ffffff;
}

.pwt-scrollbar-styles::-webkit-scrollbar-track:hover {
  background: #e0e0e0;
}

.pwt-scrollbar-styles::-webkit-scrollbar-thumb {
  background: #ffffff;
}

.pwt-scrollbar-styles::-webkit-scrollbar-thumb:hover {
  background: #eeeeee;
}

.modal-content {
  background-color: rgb(var(--v-theme-pwt_snow));
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  overflow: hidden;
}

.side-nav-column {
  width: 280px;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 20px 0px 20px 20px;
  border-right: 2px solid rgb(var(--v-theme-pwt_off_white));
  background-color: rgb(var(--v-theme-pwt_snow));
  scroll-behavior: smooth;
}

.display-column {
  width: calc(100% - 280px);
  height: 100%;
  background-color: rgb(var(--v-theme-pwt_snow));
}

.display-column--mobile {
  width: 100%;
}

.display-column--scroll-container {
  height: calc(100% - 130px);
  overflow-y: auto;
  scroll-behavior: smooth;
}

.display-column--scroll-container--home-tab {
  height: calc(100% - 60px);
  overflow-y: auto;
  scroll-behavior: smooth;
}

.exit-nav-bar {
  background-color: rgb(var(--v-theme-pwt_snow));
  padding: 20px;
  display: flex;
  flex-direction: row;
  justify-content: end;
  height: 60px;
}

.sticky-nav {
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: rgb(var(--v-theme-pwt_snow));
  height: 70px;
}

* :deep(.v-expansion-panel-title--active) {
  min-height: 48px !important;
  height: 48px !important;
  max-height: 48px !important;
}
</style>
